// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CaseDescriptionSider from '@Organisms/Case/Siders/Description';
import client from '@Client';
import useOneQuery from '@Hooks/useOneQuery';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout, useStorageCollapse } from '@Molecules/THLayout';
import { computeEventList } from '@Organisms/Case/eventList';
import { computeGetSelector } from '@Organisms/Case/selector';
import { CFList } from '@Molecules/CFList/CFList';
import { CaseOutput, CaseStatusStage, QuerySelector, CommentOutput, PermissionsEnum } from 'thehive-sdk';
import { THForm } from '@Molecules/THForm/THForm';
import { Form } from 'antd';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { onCaseImageUpload } from '@Helpers/download';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { ICONS } from '@Constants/iconsApp';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { mergeCF } from '@Helpers/customField';
import { mergeTag } from '@Helpers/array';
import { ScopeContext } from '@Context/ScopeProvider';
import useListQuery from '@Hooks/useListQuery';
import { trimTags } from '@Helpers/tags';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { useCurrentUser } from '@Queries/userQueries';
import usePageTitle from '@Hooks/usePageTitle';

import './Details.scss';

const refItemsPerPage = 20;

const computeSelectorComment = (caseId: string): QuerySelector => ({
    head: [{ name: 'getCase', idOrName: caseId }, { name: 'allComments' }],
    extraData: ['links'],
    name: `get-case-comments-${caseId}-function`,
    sort: ['-_createdAt'],
});

const CaseDescription = (): ReactElement => {
    const i = useLocalMessage();
    const { caseId } = useParams<{ caseId: string }>();
    const [form] = Form.useForm();
    const { data: currentUser } = useCurrentUser();

    const [itemsPerPage, setItemsPerPage] = useState(refItemsPerPage);
    const [currentData, setCurrentData] = useState<CaseOutput>();
    const [updatedData, setUpdatedData] = useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);
    const [activeKeyCF, setActiveKeyCF] = useState('0');

    const [editMode, setEditMode] = React.useState(false);

    const { data: caseData, refetch: caseRefetch } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const { data: dataComment = [], refetch: listRefetchComment } = useListQuery<CommentOutput[]>({
        controller: client.CommentController,
        queryParams: {
            paginationOptions: { page: 0, itemsPerPage },
            selector: computeSelectorComment(caseId),
            sort: ['-_createdAt'],
        },
    });

    const {
        collapse: { opened },
    } = useStorageCollapse();

    const { customFieldList } = useContext(ScopeContext);

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    setForceUpdate(true);
                    if (message) {
                        openMessage('success', message);
                    }
                    caseRefetch();
                },
            }),
        [caseRefetch, i],
    );

    const { userPermissions = [] } = caseData || {};

    const hasPermissions = useSplitPermissions(userPermissions, [
        PermissionsEnum.MANAGE_CASE_UPDATE,
        PermissionsEnum.MANAGE_COMMENT,
    ]);

    const { stage = '' } = currentData || {};

    const editCase = useCallback(
        ({ customFields, tags, ...values }) => {
            const treatedCF = customFields
                ? mergeCF(currentData?.customFields || [], caseData?.customFields || [], customFields)
                : [];

            const treatedTag = tags ? mergeTag(currentData?.tags || [], caseData?.tags || [], tags) : [];

            eventList.onEdit(
                {
                    ...values,
                    ...(customFields ? { customFields: treatedCF } : {}),
                    ...(tags ? { tags: trimTags(treatedTag) } : {}),
                },
                caseId,
            );

            setEditMode(false);
        },
        [caseData, caseId, currentData, eventList],
    );

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.CASE },
        (data) => {
            const canRefetch = data.some(
                (audit) => audit.base.action === 'update' && audit.base._createdBy !== currentUser?.login,
            );

            if (canRefetch) {
                caseRefetch();
            }
        },
        [editMode, currentUser?.login],
    );

    useStreamObserver({ objectType: STREAM_EVENTS.COMMENT }, (data): void => {
        const canRefetch = data.some((action) => action.base.rootId === caseId);
        if (canRefetch) {
            listRefetchComment();
        }
    });

    useEffect(() => {
        form.setFieldsValue(currentData);
        form.resetFields();
    }, [currentData]); // eslint-disable-line

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(caseData);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode]);

    useEffect(() => {
        if ((!editMode && caseData) || forceUpdate) {
            setCurrentData(caseData);
            setForceUpdate(false);
        } else {
            setUpdatedData(true);
        }
    }, [caseData, editMode, forceUpdate]); // eslint-disable-line

    usePageTitle(i('page-title.cases.details', { number: currentData?.number || 0, title: currentData?.title || '' }));

    return (
        <THLayout>
            <THLayout.Content>
                <THForm<CaseOutput>
                    name="case-description-form"
                    form={form}
                    editable
                    initialValues={{
                        ...currentData,
                        customFields: currentData?.customFields.sort(({ value: valueA }, { value: valueB }) =>
                            `${valueA}`.localeCompare(`${valueB}`),
                        ),
                    }}
                    className="case-description__form"
                    onFinish={editCase}
                    submitTouchedValues={['customFields']}
                    onValuesChange={() => setEditMode(true)}
                >
                    <div className="case-description__body">
                        <div className="case-description__body__content">
                            <THForm.Item
                                label={i('common.title')}
                                name="title"
                                rules={[
                                    {
                                        required: hasPermissions.updateCase,
                                        message: i('app.general.required'),
                                    },
                                ]}
                            >
                                <THInput
                                    borderless
                                    disabled={!hasPermissions.updateCase}
                                    placeholder={i('common.title')}
                                />
                            </THForm.Item>

                            <THForm.Item label={i('common.tags')} name="tags">
                                <THTagSelector.Autocomplete
                                    borderless
                                    disabled={!hasPermissions.updateCase}
                                    placeholder={i('common.tags')}
                                />
                            </THForm.Item>

                            <THForm.Item label={i('common.description')} name="description">
                                <THMarkdown
                                    borderless
                                    disabled={!hasPermissions.updateCase}
                                    onImageUpload={onCaseImageUpload(caseId)}
                                    formEditMode={editMode}
                                />
                            </THForm.Item>

                            {stage === CaseStatusStage.CLOSED && (
                                <div className="case-drawer__resolution-status__container">
                                    <THForm.Item
                                        name="summary"
                                        label={i('common.summary')}
                                        className="case-drawer__resolution-status__summary"
                                    >
                                        <THMarkdown
                                            borderless
                                            disabled={!hasPermissions.updateCase}
                                            onImageUpload={onCaseImageUpload(caseId)}
                                        />
                                    </THForm.Item>
                                </div>
                            )}

                            <THForm.Item name="customFields">
                                <CFList
                                    form={form}
                                    customFields={customFieldList}
                                    borderless
                                    disabled={!hasPermissions.updateCase}
                                    activeKeyState={{
                                        activeKey: activeKeyCF,
                                        setActiveKey: setActiveKeyCF,
                                    }}
                                />
                            </THForm.Item>
                        </div>
                    </div>

                    <div className="case-description__footer">
                        <FormSubmitButton
                            warning
                            formName="case-description-form"
                            disabled={false}
                            onCancel={() => {
                                form.resetFields();
                                setEditMode(false);
                            }}
                            hidden={!editMode}
                        />
                    </div>
                </THForm>
            </THLayout.Content>

            <THLayout.Sider
                width="25vw"
                initialCollapse={opened.includes('case-comment')}
                className="case-description__wrapper"
                name="case-comment"
            >
                <CaseDescriptionSider
                    data={currentData}
                    readOnly={!hasPermissions.comment}
                    dataComment={dataComment}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    onListRefetch={() => {
                        listRefetchComment();
                    }}
                />
            </THLayout.Sider>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.description',
        icon: <ICONS.CaseIcon />,
    },
    path: RouteBuilder.compute('cases.details'),
    content: <CaseDescription />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.details' },
};
